import { Button, Stack } from '@mui/material'
import { HandlingDirection } from '@storage/app/api'
import { SelectOption } from '@storage/app/models'
import { useStores } from '@storage/hooks/use-stores.hook'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { OccupancyItem, StockOccupancy } from '../interfaces/occupancy-item'
import MultipleChipSelect from './MultipleChipSelect'

interface GeneralCargoIdentifiableItemsProps {
  plannedItems: string[]
  occupancyItemId: string
  handlingDirection: HandlingDirection
}

const GeneralCargoIdentifiableItems = observer(
  ({ plannedItems, occupancyItemId, handlingDirection }: GeneralCargoIdentifiableItemsProps) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { generalCargoStorageTrackerUIStore } = useStores()

    const occupancyItems = generalCargoStorageTrackerUIStore.occupancyItems as StockOccupancy[]

    const [selectedItems, setSelectedItems] = useState<string[]>([])

    const updateOccupancyItem = useCallback(
      (occupancyItemId: string, newProperties: { unitId: string }[]) => {
        const newQuantity = newProperties.length
        generalCargoStorageTrackerUIStore.updateOccupancyItem(
          occupancyItemId,
          'newProperties' as keyof OccupancyItem,
          newProperties,
        )
        generalCargoStorageTrackerUIStore.updateOccupancyItem(
          occupancyItemId,
          'newQuantity' as keyof OccupancyItem,
          newQuantity,
        )
      },
      [generalCargoStorageTrackerUIStore],
    )

    const existingUnitIds = useMemo(() => {
      return occupancyItems.flatMap(oi => oi.properties?.map(p => p.unitId) ?? [])
    }, [occupancyItems])

    const otherSelectedUnitIds = useMemo(() => {
      return occupancyItems
        .filter(oi => oi.id !== occupancyItemId)
        .flatMap(oi => oi.newProperties.map(p => p.unitId))
    }, [occupancyItemId, occupancyItems])

    const disabledItems = useMemo(() => {
      if (handlingDirection !== HandlingDirection.Inbound) return []

      return [
        ...existingUnitIds,
        ...otherSelectedUnitIds.filter(item => !selectedItems.includes(item)),
      ]
    }, [existingUnitIds, handlingDirection, otherSelectedUnitIds, selectedItems])

    useEffect(() => {
      const currentOccupancyItem = occupancyItems.find(oi => oi.id === occupancyItemId)
      if (currentOccupancyItem) {
        const nextSelectedItems = currentOccupancyItem.newProperties.map(p => p.unitId)
        setSelectedItems(
          handlingDirection === HandlingDirection.Inbound
            ? nextSelectedItems.filter(item => !existingUnitIds.includes(item))
            : nextSelectedItems,
        )
      }
    }, [occupancyItemId, occupancyItems, handlingDirection, existingUnitIds])

    const handleSelectedItemsChange = useCallback(
      (newSelectedItems: string[]) => {
        const sanitizedItems =
          handlingDirection === HandlingDirection.Inbound
            ? newSelectedItems.filter(item => !existingUnitIds.includes(item))
            : newSelectedItems

        setSelectedItems(sanitizedItems)
        updateOccupancyItem(
          occupancyItemId,
          sanitizedItems.map(item => ({ unitId: item })),
        )
      },
      [handlingDirection, existingUnitIds, occupancyItemId, updateOccupancyItem],
    )

    const itemsOptions: SelectOption[] = useMemo(() => {
      if (handlingDirection === HandlingDirection.Inbound) {
        return plannedItems?.map(plannedItem => ({
          label: plannedItem,
          value: plannedItem,
        }))
      } else {
        const currentOccupancyItem = occupancyItems.find(oi => oi.id === occupancyItemId)
        return (
          currentOccupancyItem?.properties.map(prop => ({
            label: prop.unitId,
            value: prop.unitId,
          })) || []
        )
      }
    }, [plannedItems, handlingDirection, occupancyItemId, occupancyItems])

    useEffect(() => {
      if (handlingDirection === HandlingDirection.Inbound) {
        return
      }

      const currentOccupancyItem = occupancyItems.find(oi => oi.id === occupancyItemId)
      if (!currentOccupancyItem) {
        return
      }

      // Only prefill once to avoid overriding manual changes.
      if (currentOccupancyItem.newProperties.length > 0) {
        return
      }

      const availableUnitIds = currentOccupancyItem.properties?.map(x => x.unitId) ?? []
      const matchingPlannedItems = plannedItems.filter(item => availableUnitIds.includes(item))

      if (matchingPlannedItems.length) {
        handleSelectedItemsChange(matchingPlannedItems)
      }
    }, [
      plannedItems,
      handlingDirection,
      occupancyItemId,
      occupancyItems,
      handleSelectedItemsChange,
    ])

    const handleSelectAll = () => {
      const allSelectableValues = itemsOptions
        .filter(option => !disabledItems.includes(option.value))
        .map(option => option.value)

      setSelectedItems(allSelectableValues)

      updateOccupancyItem(
        occupancyItemId,
        allSelectableValues.map(item => ({ unitId: item })),
      )
    }

    const handleDeselectAll = () => {
      setSelectedItems([])
      updateOccupancyItem(occupancyItemId, [])
    }

    return (
      <>
        <Stack direction='row' gap={theme.customSpacing.xl}>
          <MultipleChipSelect
            items={itemsOptions?.map(option => option.label)}
            selectedItems={selectedItems}
            disabledItems={disabledItems}
            onChange={handleSelectedItemsChange}
          />
        </Stack>

        <Stack direction='row'>
          <Button variant='text' color='primary' onClick={handleSelectAll}>
            {t('selectAll', 'Select All')}
          </Button>
          <Button variant='text' color='secondary' onClick={handleDeselectAll}>
            {t('deselectAll', 'Deselect All')}
          </Button>
        </Stack>
      </>
    )
  },
)

export default GeneralCargoIdentifiableItems
